import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, defer, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthEventService } from 'src/app/user/shared/services/auth-event.service';
import { SessionService } from 'src/app/user/shared/services/session.service';
import { JwtTokenService } from './jwt-token.service';

export type LogoutReason = 'manual' | 'refresh_failed' | 'unauthorized';

interface RefreshTokenResponse {
  jwtToken: string;
}

type RefreshMessageType = 'refresh-started' | 'refresh-success' | 'refresh-failed';

interface RefreshMessage {
  type: RefreshMessageType;
  tabId: string;
  timestamp: number;
  status?: number;
}

interface RefreshLock {
  tabId: string;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthTokenService implements OnDestroy {
  private readonly refreshThresholdMs = 5 * 60 * 1000;
  private readonly refreshLockTimeoutMs = 15 * 1000;
  private readonly refreshWaitTimeoutMs = 20 * 1000;
  private readonly refreshLockKey = 'auth-refresh-lock';
  private readonly refreshChannelName = 'auth-token-refresh';
  private readonly tabId = `auth-tab-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

  private refreshInFlight$?: Observable<string>;
  private refreshChannel?: BroadcastChannel;
  private readonly refreshMessages$ = new Subject<RefreshMessage>();

  constructor(
    private readonly http: HttpClient,
    private readonly jwtTokenService: JwtTokenService,
    private readonly authEventService: AuthEventService,
    private readonly sessionService: SessionService,
    private readonly router: Router
  ) {
    this.setupBroadcastChannel();
  }

  public getValidAccessToken(forceRefresh: boolean = false): Observable<string> {
    const token = this.jwtTokenService.getJwtToken();

    if (!token) {
      return throwError(() => 'Kein JWT-Token im local Storage');
    }

    if (!forceRefresh &&
      this.jwtTokenService.isTokenValid(token) &&
      !this.jwtTokenService.isTokenExpiringSoon(token, this.refreshThresholdMs)) {
      return of(token);
    }

    return this.refreshTokenOnce();
  }

  public refreshTokenOnce(): Observable<string> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = defer(() => {
      if (this.hasFreshOtherTabRefreshLock()) {
        return this.waitForOtherTabRefresh().pipe(
          catchError(error => this.isRefreshWaitTimeout(error)
            ? this.refreshFromApi()
            : throwError(() => error))
        );
      }

      return this.refreshFromApi();
    }).pipe(
      finalize(() => {
        this.refreshInFlight$ = undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.refreshInFlight$;
  }

  public logout(reason: LogoutReason = 'manual'): void {
    var token = this.jwtTokenService.getJwtTokenIfValid();
    if (token != null) {
      var pHeaders = new HttpHeaders();
      pHeaders = pHeaders.set('Authorization', `Bearer ${token}`);
 
      this.http.post(
              `${environment.apiAddress}User/revoke-token`, 
              null,
              { headers: pHeaders, withCredentials: true }).subscribe(
                  () => { this.doLogout(reason);},
                  (error) => {this.doLogout(reason);}
      );
    }
    else
      this.doLogout(reason)
  }

  private doLogout(reason:string): void
  {
    this.authEventService.emitLogout();
    this.sessionService.logout();
    this.jwtTokenService.removeJwtToken();

    if (reason === 'manual') {
      this.router.navigateByUrl('login');
      return;
    }

    this.router.navigate(['/login'], {
      queryParams: { reason }
    });
  }

  private refreshFromApi(): Observable<string> {
    return defer(() => {
      if (!this.tryAcquireRefreshLock()) {
        return this.waitForOtherTabRefresh().pipe(
          catchError(error => this.isRefreshWaitTimeout(error)
            ? this.refreshFromApi()
            : throwError(() => error))
        );
      }

      this.broadcastRefreshMessage({ type: 'refresh-started' });

      return this.http.get<RefreshTokenResponse>(
        `${environment.apiAddress}User/refresh-token`,
        { withCredentials: true }
      ).pipe(
        map(response => {
          if (!response?.jwtToken) {
            throw new Error('Refresh response contains no JWT token');
          }

          this.jwtTokenService.setJwtToken(response.jwtToken);
          this.broadcastRefreshMessage({ type: 'refresh-success' });

          return response.jwtToken;
        }),
        catchError(error => {
          this.broadcastRefreshMessage({
            type: 'refresh-failed',
            status: error?.status
          });

          if (error?.status === 401 || error?.status === 403) {
            this.logout('refresh_failed');
          }

          return throwError(() => error);
        }),
        finalize(() => {
          this.clearRefreshLock();
        })
      );
    });
  }

  private waitForOtherTabRefresh(): Observable<string> {
    return new Observable<string>(observer => {
      const timeout = window.setTimeout(() => {
        observer.error({ code: 'refresh_wait_timeout' });
      }, this.refreshWaitTimeoutMs);

      const subscription = this.refreshMessages$.subscribe(message => {
        if (message.type === 'refresh-success') {
          const token = this.jwtTokenService.getJwtToken();

          if (token && this.jwtTokenService.isTokenValid(token)) {
            observer.next(token);
            observer.complete();
          } else {
            observer.error({ code: 'refresh_success_without_valid_token' });
          }
        }

        if (message.type === 'refresh-failed') {
          observer.error({
            code: 'refresh_failed',
            status: message.status
          });
        }
      });

      return () => {
        window.clearTimeout(timeout);
        subscription.unsubscribe();
      };
    });
  }

  private tryAcquireRefreshLock(): boolean {
    const lock = this.getRefreshLock();
    const now = Date.now();

    if (lock && lock.expiresAt > now && lock.tabId !== this.tabId) {
      return false;
    }

    const ownLock: RefreshLock = {
      tabId: this.tabId,
      expiresAt: now + this.refreshLockTimeoutMs
    };

    localStorage.setItem(this.refreshLockKey, JSON.stringify(ownLock));

    return this.getRefreshLock()?.tabId === this.tabId;
  }

  private clearRefreshLock(): void {
    if (this.getRefreshLock()?.tabId === this.tabId) {
      localStorage.removeItem(this.refreshLockKey);
    }
  }

  private hasFreshOtherTabRefreshLock(): boolean {
    const lock = this.getRefreshLock();
    return !!lock && lock.expiresAt > Date.now() && lock.tabId !== this.tabId;
  }

  private getRefreshLock(): RefreshLock | null {
    const rawLock = localStorage.getItem(this.refreshLockKey);
    if (!rawLock) {
      return null;
    }

    try {
      const lock = JSON.parse(rawLock) as RefreshLock;
      if (!lock.tabId || typeof lock.expiresAt !== 'number') {
        return null;
      }

      return lock;
    } catch (error) {
      localStorage.removeItem(this.refreshLockKey);
      return null;
    }
  }

  private setupBroadcastChannel(): void {
    if (!window.BroadcastChannel) {
      return;
    }

    this.refreshChannel = new BroadcastChannel(this.refreshChannelName);
    this.refreshChannel.onmessage = (event: MessageEvent<RefreshMessage>) => {
      const message = event.data;
      if (!message || message.tabId === this.tabId) {
        return;
      }

      this.refreshMessages$.next(message);
    };
  }

  private broadcastRefreshMessage(message: Pick<RefreshMessage, 'type' | 'status'>): void {
    if (!this.refreshChannel) {
      return;
    }

    this.refreshChannel.postMessage({
      ...message,
      tabId: this.tabId,
      timestamp: Date.now()
    });
  }

  private isRefreshWaitTimeout(error: any): boolean {
    return error?.code === 'refresh_wait_timeout';
  }

  public ngOnDestroy(): void {
    this.refreshChannel?.close();
    this.refreshMessages$.complete();
  }
}
